/**
 * Build a kitchen "fridge surround" — two full-height vertical panels
 * flanking the fridge slot. The fridge slides in between the panels;
 * an over-fridge upper cabinet (built separately by the materializer)
 * fills the space above the fridge.
 *
 * Width adapts to the fridge: surround width = fridge body width + a
 * ½" side panel on each side (30" fridge → 31" surround, 36" fridge →
 * 37", 48" fridge → 49"). The 3D renderer reads
 * `dimensions.sidePanelThickness` to draw the two narrow panels at
 * each X edge of the bounding box.
 */

const { FRIDGE_SIDE_PANEL_THICKNESS } = require('./constants');
const { getWallAxes } = require('./wallMath');

/**
 * The panel thickness either side of a fridge placement: the cabinet family's
 * own when the generator recorded one (`panelThickness` — Venus's 1½" end
 * panel, `cabinet-runs/fridgeColumn.js`), else our ½" surround.
 */
function fridgeSidePanelOf(placement) {
  return placement?.panelThickness ?? FRIDGE_SIDE_PANEL_THICKNESS;
}

/** Surround footprint along the wall for a given fridge placement. */
function fridgeSurroundWidthFor(
  placement,
  sidePanelThickness = fridgeSidePanelOf(placement)
) {
  return (placement.width || 0) + 2 * sidePanelThickness;
}

/**
 * @param {Object} args
 * @param {Object} args.placement - The fridge candidate placement.
 * @param {Object} args.wall - Wall the fridge is on.
 * @param {number} args.floorY - World Y of the floor surface.
 * @param {number} args.ceilingY - World Y of the ceiling surface.
 * @param {string} [args.fixtureGroupId] - Optional shared groupId so the
 *   surround panels and the over-fridge cabinet are treated as one
 *   installation (skips fixture-group collision pairs).
 * @param {number} [args.sidePanelThickness] - Each panel's thickness; our
 *   ½" unless a vendor family sells its own end panel
 *   (`cabinet-runs/fridgeColumn.js`: Venus's is 1½").
 * @param {number} [args.depth] - The panels' depth off the wall; the fridge's
 *   own unless a vendor panel sets it (Venus's is 24", the fridge proud).
 * @returns {Object|null} A single 'fridge-surround' object, or null on
 *   degenerate input.
 */
function buildFridgeSurround({
  placement,
  wall,
  floorY,
  ceilingY,
  fixtureGroupId,
  sidePanelThickness = FRIDGE_SIDE_PANEL_THICKNESS,
  depth = placement.depth,
}) {
  const axes = getWallAxes(wall);
  if (!axes) return null;
  const ry = axes.wallRotationY;

  const wallParam = (placement.range.start + placement.range.end) / 2;
  const surroundHeight = ceilingY - floorY;
  if (surroundHeight <= 0.05) return null;

  const surroundWidth = fridgeSurroundWidthFor(placement, sidePanelThickness);
  const surroundDepth = depth;
  const center = {
    x:
      axes.start.x +
      wallParam * axes.wallDir.x +
      (surroundDepth / 2) * axes.wallNormal.x,
    y: floorY + surroundHeight / 2,
    z:
      axes.start.z +
      wallParam * axes.wallDir.z +
      (surroundDepth / 2) * axes.wallNormal.z,
  };

  const stamp = `${placement.applianceId}`;

  return {
    id: `fridge_surround_${stamp}`,
    type: 'fridge-surround',
    label: 'Fridge Surround',
    category: { 'fridge-surround': true },
    center,
    dimensions: {
      x: surroundWidth,
      y: surroundHeight,
      z: surroundDepth,
      sidePanelThickness,
    },
    rotation: { x: 0, y: ry, z: 0 },
    parentWallIds: [wall.id],
    fixtureGroupId: fixtureGroupId || `kitchenFridgeSurround_${stamp}`,
    customFrontClearance: 0,
    addedBy: 'kitchen-layout',
    lastModified: new Date().toISOString(),
  };
}

module.exports = {
  buildFridgeSurround,
  fridgeSurroundWidthFor,
  fridgeSidePanelOf,
};
